/**
 * Site detail page.
 *
 * Phase 16: Product Intelligence UX.
 *
 * Shows:
 * - KPI strip (score, issues, pages, active status)
 * - Health summary (recommendation based on latest scan)
 * - Site info and WCAG scan config
 * - "Run scan" button (triggers a new scan)
 * - Scan history table with score, status badges and drill-down links
 * - Link to compliance report
 *
 * API endpoints used:
 *   GET  /sites/:id          — site detail with config
 *   GET  /sites/:id/scans    — scan history (paginated)
 *   POST /scans              — trigger new scan
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { api, ApiError, friendlyError } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import {
  estimateAccessibilityScore,
  getScoreColor,
  getScoreLabelKey,
  getScoreVariant,
} from '../lib/accessibility-score';
import FeatureLockModal from '../components/entitlements/FeatureLockModal';
import LockedFeatureState from '../components/entitlements/LockedFeatureState';
import AuthenticatedScanAccessCard from '../components/sites/AuthenticatedScanAccessCard';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge, { BadgeVariant } from '../components/ui/Badge';
import Container from '../components/ui/Container';
import EmptyState from '../components/ui/EmptyState';
import PageHeader from '../components/ui/PageHeader';
import AlertBanner from '../components/ui/AlertBanner';
import SectionHeader from '../components/ui/SectionHeader';
import SectionTabs from '../components/ui/SectionTabs';
import RecommendationPanel from '../components/ui/RecommendationPanel';
import { buildBillingUpgradePath, getPlanLabel } from '../lib/plans';
import { resolveScanFailurePresentation, resolveScanFailureSummary } from '../lib/scan-failures';
import { formatDateTime } from '../i18n/formatters';
import ManualIssueModal, { type ManualIssueModalSubmitInput } from '../components/manual-issues/ManualIssueModal';
import ManualIssuesTable from '../components/manual-issues/ManualIssuesTable';
import { dispatchWorkspaceDataChanged, subscribeToWorkspaceDataChanges } from '../lib/workspace-data-events';
import type {
  AlertPreferenceMap,
  AlertPreferenceProfile,
  ManualIssueListItem,
  OperatorAuditEvent,
  SiteEvidenceRetentionSummary,
  SiteWithConfig,
  Scan,
  ScanAuthMetadata,
  ScanComparison,
} from '@accestra/types';

// -------------------------------------------------------------------
// Schedule frequency presets
// -------------------------------------------------------------------

function getFrequencyPresets() {
  return [
    { label: i18n.t('site:schedule.presets.daily'), cron: '0 2 * * *' },
    { label: i18n.t('site:schedule.presets.weekly'), cron: '0 2 * * 1' },
    { label: i18n.t('site:schedule.presets.monthly'), cron: '0 2 1 * *' },
    { label: i18n.t('site:schedule.presets.custom'), cron: 'custom' },
  ];
}

interface ScansResponse {
  scans: Scan[];
  pagination: {
    total: number;
    uncappedTotal?: number;
    page: number;
    pageSize: number;
  };
}

interface OrgUsageResponse {
  plan: string;
  limits: {
    scheduledScansEnabled: boolean;
    authenticatedScanningEnabled: boolean;
    regressionHistoryDepth: number;
  };
}

interface SiteActivityResponse {
  events: OperatorAuditEvent[];
}

interface SiteRetentionResponse {
  summary: SiteEvidenceRetentionSummary;
}

interface SiteAlertPreferencesResponse {
  preferences: AlertPreferenceProfile;
}

interface ManualIssuesResponse {
  issues: ManualIssueListItem[];
}

type SiteWorkspaceSection =
  | 'overview'
  | 'issues'
  | 'scan-history'
  | 'auth-session'
  | 'scheduled-jobs'
  | 'evidence'
  | 'activity';

// -------------------------------------------------------------------
// Status → Badge variant
// -------------------------------------------------------------------

function statusVariant(status: string): BadgeVariant {
  switch (status) {
    case 'completed':               return 'success';
    case 'completed_with_failures': return 'warning';
    case 'running':                 return 'warning';
    case 'queued':                  return 'info';
    case 'failed':                  return 'danger';
    case 'auth_failed':             return 'danger';
    default:                        return 'neutral';
  }
}

function getScanAuthMetadata(scan: Scan): ScanAuthMetadata | null {
  const candidate = scan.metadata?.['auth'];
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
    return null;
  }

  const value = candidate as Record<string, unknown>;
  const mode = value['mode'];
  if (
    mode !== 'public' &&
    mode !== 'bearer_token' &&
    mode !== 'session_storage_state' &&
    mode !== 'form_login'
  ) {
    return null;
  }

  return {
    mode,
    usedAuthenticatedMode: value['usedAuthenticatedMode'] === true || mode !== 'public',
    blockedByAuthFailure: value['blockedByAuthFailure'] === true,
    failureCategory: typeof value['failureCategory'] === 'string'
      ? value['failureCategory'] as ScanAuthMetadata['failureCategory']
      : null,
    message: typeof value['message'] === 'string' ? value['message'] : null,
    consoleHint: typeof value['consoleHint'] === 'string' ? value['consoleHint'] : null,
    networkHint: typeof value['networkHint'] === 'string' ? value['networkHint'] : null,
    refreshAttempted: value['refreshAttempted'] === true,
    refreshSucceeded: value['refreshSucceeded'] === true,
    screenshotAvailable: value['screenshotAvailable'] === true,
    authenticatedPagesScanned:
      typeof value['authenticatedPagesScanned'] === 'number'
        ? value['authenticatedPagesScanned']
        : 0,
    discoveredRouteCount:
      typeof value['discoveredRouteCount'] === 'number'
        ? value['discoveredRouteCount']
        : 0,
  };
}

function formatAuthMode(mode: ScanAuthMetadata['mode']): string {
  switch (mode) {
    case 'public': return i18n.t('site:auth.modes.public');
    case 'bearer_token': return i18n.t('site:auth.modes.bearerToken');
    case 'session_storage_state': return i18n.t('site:auth.modes.sessionStorageState');
    case 'form_login': return i18n.t('site:auth.modes.formLogin');
    default: return String(mode).replace(/_/g, ' ');
  }
}

function formatAuthFailureCategory(category: ScanAuthMetadata['failureCategory']): string | null {
  if (!category) return null;
  switch (category) {
    case 'session_expired': return i18n.t('site:auth.failures.sessionExpired');
    case 'redirected_to_login': return i18n.t('site:auth.failures.redirectedToLogin');
    case 'redirected_to_login_mid_scan': return i18n.t('site:auth.failures.redirectedToLoginMidScan');
    case 'selector_missing': return i18n.t('site:auth.failures.selectorMissing');
    case 'success_selector_not_found': return i18n.t('site:auth.failures.successSelectorNotFound');
    case 'selector_not_found': return i18n.t('site:auth.failures.selectorNotFound');
    case 'auth_token_rejected': return i18n.t('site:auth.failures.authTokenRejected');
    case 'auth_session_invalidated': return i18n.t('site:auth.failures.authSessionInvalidated');
    default: return category.replace(/_/g, ' ');
  }
}

function formatScanStatus(status: string | null | undefined): string {
  if (!status) return i18n.t('site:scanStatuses.fallback');
  switch (status) {
    case 'completed':
    case 'completed_with_failures':
    case 'running':
    case 'queued':
    case 'failed':
    case 'auth_failed':
      return i18n.t(`site:scanStatuses.${status}` as const);
    default:
      return status.replace(/_/g, ' ');
  }
}

function formatScanType(scanType: string): string {
  switch (scanType) {
    case 'manual':
      return i18n.t('site:scanHistory.types.manual');
    case 'scheduled':
      return i18n.t('site:scanHistory.types.scheduled');
    default:
      return scanType;
  }
}

function requiresReauthentication(category: ScanAuthMetadata['failureCategory']): boolean {
  return category === 'session_expired' ||
    category === 'redirected_to_login' ||
    category === 'redirected_to_login_mid_scan' ||
    category === 'selector_missing' ||
    category === 'auth_session_invalidated' ||
    category === 'auth_token_rejected';
}

function formatRegressionState(
  state: 'baseline' | 'improved' | 'stable' | 'regressed' | undefined,
): string {
  switch (state) {
    case 'regressed': return i18n.t('site:regression.regressed');
    case 'improved': return i18n.t('site:regression.improved');
    case 'baseline': return i18n.t('site:regression.baseline');
    case 'stable':
    default:
      return i18n.t('site:regression.stable');
  }
}

function formatRegressionSummary(
  state: 'baseline' | 'improved' | 'stable' | 'regressed' | undefined,
): string {
  switch (state) {
    case 'baseline':
      return i18n.t('common:regressionSummary.baseline');
    case 'improved':
      return i18n.t('common:regressionSummary.improved');
    case 'regressed':
      return i18n.t('common:regressionSummary.regressed');
    case 'stable':
    default:
      return i18n.t('common:regressionSummary.stable');
  }
}

function formatAuditAction(action: string): string {
  switch (action) {
    case 'site.schedule_updated': return i18n.t('site:activity.actions.scheduleUpdated');
    case 'site.auth_profile_saved': return i18n.t('site:activity.actions.authProfileSaved');
    case 'site.auth_profile_tested': return i18n.t('site:activity.actions.authProfileTested');
    case 'site.auth_profile_deleted': return i18n.t('site:activity.actions.authProfileDeleted');
    case 'issue.workflow_state_changed': return i18n.t('site:activity.actions.workflowStateChanged');
    case 'issue.assignee_changed': return i18n.t('site:activity.actions.assigneeChanged');
    case 'issue.workflow_note_changed': return i18n.t('site:activity.actions.workflowNoteChanged');
    case 'issue.suppression_created': return i18n.t('site:activity.actions.suppressionCreated');
    case 'issue.suppression_cleared': return i18n.t('site:activity.actions.suppressionCleared');
    default: return action.replace(/[._]/g, ' ');
  }
}

function describeAuditEvent(event: OperatorAuditEvent): string {
  const metadata = event.metadata ?? {};
  switch (event.action) {
    case 'site.schedule_updated':
      return metadata['scheduleChanged'] === true
        ? i18n.t('site:activity.summary.scheduleUpdated')
        : i18n.t('site:activity.summary.scheduleChanged');
    case 'site.auth_profile_tested':
      return typeof metadata['newStatus'] === 'string'
        ? i18n.t('site:activity.summary.authTestResult', {
          status: formatScanStatus(String(metadata['newStatus'])),
        })
        : i18n.t('site:activity.summary.authTestRecorded');
    case 'issue.suppression_created':
      return typeof metadata['reason'] === 'string'
        ? i18n.t('site:activity.summary.suppressionReason', { reason: metadata['reason'] })
        : i18n.t('site:activity.summary.suppressionCreated');
    case 'issue.suppression_cleared':
      return i18n.t('site:activity.summary.suppressionCleared');
    case 'issue.workflow_state_changed':
      return typeof metadata['newStatus'] === 'string'
        ? i18n.t('site:activity.summary.workflowMoved', {
          status: formatScanStatus(String(metadata['newStatus'])),
        })
        : i18n.t('site:activity.summary.workflowChanged');
    case 'issue.assignee_changed':
      return typeof metadata['newAssigneeEmail'] === 'string'
        ? i18n.t('site:activity.summary.assignedTo', { email: metadata['newAssigneeEmail'] })
        : i18n.t('site:activity.summary.assigneeChanged');
    default:
      return i18n.t('site:activity.summary.default');
  }
}

function preferenceLabel(key: keyof AlertPreferenceMap): string {
  switch (key) {
    case 'scanCompleted': return i18n.t('site:alertPreferences.labels.scanCompleted');
    case 'scanFailed': return i18n.t('site:alertPreferences.labels.scanFailed');
    case 'authOperationalFailure': return i18n.t('site:alertPreferences.labels.authOperationalFailure');
    case 'issueRegression': return i18n.t('site:alertPreferences.labels.issueRegression');
    case 'issueReappeared': return i18n.t('site:alertPreferences.labels.issueReappeared');
    default: return key;
  }
}

function preferenceHint(key: keyof AlertPreferenceMap): string {
  switch (key) {
    case 'scanCompleted': return i18n.t('site:alertPreferences.hints.scanCompleted');
    case 'scanFailed': return i18n.t('site:alertPreferences.hints.scanFailed');
    case 'authOperationalFailure': return i18n.t('site:alertPreferences.hints.authOperationalFailure');
    case 'issueRegression': return i18n.t('site:alertPreferences.hints.issueRegression');
    case 'issueReappeared': return i18n.t('site:alertPreferences.hints.issueReappeared');
    default: return '';
  }
}

// -------------------------------------------------------------------
// Formatters
// -------------------------------------------------------------------

function formatDate(d: Date | string | null): string {
  if (!d) return '—';
  return formatDateTime(d) || '—';
}

function formatDuration(start: Date | string | null, end: Date | string | null): string {
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 60000) return `${Math.round(ms / 1000)}s`;
  return `${Math.round(ms / 60000)}m`;
}

// -------------------------------------------------------------------
// Design tokens
// -------------------------------------------------------------------

const C = {
  page:  'var(--acc-color-page)',
  text:  'var(--acc-color-text)',
  muted: 'var(--acc-color-text-muted)',
  link:  'var(--acc-color-primary)',
  surface: 'var(--acc-color-surface)',
  surfaceMuted: 'var(--acc-color-surface-muted)',
  border: 'var(--acc-color-border)',
  danger: 'var(--acc-color-danger)',
  success: 'var(--acc-color-success)',
};

const tbl = {
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: {
    textAlign: 'left' as const,
    padding: '14px 20px',
    background: 'var(--acc-color-surface-muted)',
    borderBottom: '1px solid var(--acc-color-border)',
    color: 'var(--acc-color-text-soft)',
    fontWeight: 700,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.12em',
  },
  td: {
    padding: '16px 20px',
    borderBottom: '1px solid var(--acc-color-border)',
    verticalAlign: 'top' as const,
  },
  link: { color: C.link, textDecoration: 'none', fontWeight: 500 },
};

// -------------------------------------------------------------------
// Page component
// -------------------------------------------------------------------

export default function SitePage() {
  const { t } = useTranslation(['site', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { role } = useAuth();
  const frequencyPresets = getFrequencyPresets();

  const [site, setSite] = useState<SiteWithConfig | null>(null);
  const [scans, setScans] = useState<Scan[]>([]);
  const [comparisons, setComparisons] = useState<Record<string, ScanComparison>>({});
  const [loadingSite, setLoadingSite] = useState(true);
  const [loadingScans, setLoadingScans] = useState(true);
  const [siteError, setSiteError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<OrgUsageResponse | null>(null);
  const [scanHistoryTotal, setScanHistoryTotal] = useState(0);
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [manualIssues, setManualIssues] = useState<ManualIssueListItem[]>([]);
  const [showManualIssueModal, setShowManualIssueModal] = useState(false);
  const [editingManualIssue, setEditingManualIssue] = useState<ManualIssueListItem | null>(null);
  const [creatingManualIssue, setCreatingManualIssue] = useState(false);
  const [manualIssueError, setManualIssueError] = useState<string | null>(null);
  const [manualIssueResetSignal, setManualIssueResetSignal] = useState(0);
  const [manualIssueSuccessMessage, setManualIssueSuccessMessage] = useState<string | null>(null);
  const [manualIssueSuccessSignal, setManualIssueSuccessSignal] = useState(0);
  const [siteActivity, setSiteActivity] = useState<OperatorAuditEvent[]>([]);
  const [retentionSummary, setRetentionSummary] = useState<SiteEvidenceRetentionSummary | null>(null);
  const [alertPreferences, setAlertPreferences] = useState<AlertPreferenceProfile | null>(null);
  const [alertPreferenceDraft, setAlertPreferenceDraft] = useState<AlertPreferenceMap | null>(null);
  const [alertPreferencesSaving, setAlertPreferencesSaving] = useState(false);
  const [alertPreferencesError, setAlertPreferencesError] = useState<string | null>(null);
  const [alertPreferencesSuccess, setAlertPreferencesSuccess] = useState<string | null>(null);

  // Schedule editing state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleCron, setScheduleCron] = useState('0 2 * * *');
  const [customCron, setCustomCron] = useState('');
  const [schedulePreset, setSchedulePreset] = useState('0 2 * * *');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSuccess, setScheduleSuccess] = useState<string | null>(null);
  const [showScheduleUpgradeModal, setShowScheduleUpgradeModal] = useState(false);

  const requestedSection = searchParams.get('section');
  const activeSection: SiteWorkspaceSection =
    requestedSection === 'issues' ||
    requestedSection === 'scan-history' ||
    requestedSection === 'scans' ||
    requestedSection === 'auth-session' ||
    requestedSection === 'authentication' ||
    requestedSection === 'scheduled-jobs' ||
    requestedSection === 'alerts' ||
    requestedSection === 'evidence' ||
    requestedSection === 'activity'
      ? (
        requestedSection === 'scans' ? 'scan-history' :
        requestedSection === 'authentication' ? 'auth-session' :
        requestedSection === 'alerts' ? 'scheduled-jobs' :
        requestedSection
      )
      : 'overview';

  const loadUsage = useCallback(async () => {
    try {
      const usage = await api.get<OrgUsageResponse>('/orgs/me/usage');
      setUsageData(usage);
    } catch {
      // Non-critical. Feature lock state falls back to backend responses if needed.
    }
  }, []);

  const loadSite = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingSite(true);
      const data = await api.get<{ site: SiteWithConfig }>(`/sites/${id}`);
      setSite(data.site);
      // Initialise schedule editing from loaded config
      if (data.site.scanConfig) {
        const presets = getFrequencyPresets();
        setScheduleEnabled(data.site.scanConfig.isScheduled);
        const cron = data.site.scanConfig.scheduleCron ?? '0 2 * * *';
        const preset = presets.find((p) => p.cron === cron && p.cron !== 'custom');
        setSchedulePreset(preset ? cron : 'custom');
        setScheduleCron(cron);
        setCustomCron(preset ? '' : cron);
      }
    } catch (err) {
      setSiteError(friendlyError(err, t('header.loadFailed')));
    } finally {
      setLoadingSite(false);
    }
  }, [id, t]);

  const loadScans = useCallback(async () => {
    if (!id) return;
    try {
      setLoadingScans(true);
        const data = await api.get<ScansResponse>(`/sites/${id}/scans?pageSize=20`);
        const fetchedScans = data.scans ?? [];
        setScans(fetchedScans);
        setScanHistoryTotal(data.pagination?.uncappedTotal ?? data.pagination?.total ?? fetchedScans.length);

      // Fetch comparisons for completed scans (fire-and-forget, non-blocking)
      const completedScanIds = fetchedScans
        .filter((s) => s.status === 'completed' || s.status === 'completed_with_failures')
        .map((s) => s.id)
        .slice(0, 10);

      const compMap: Record<string, ScanComparison> = {};
      await Promise.allSettled(
        completedScanIds.map(async (scanId) => {
          try {
            const cmp = await api.get<{ comparison: ScanComparison }>(`/scans/${scanId}/comparison`);
            if (cmp.comparison) compMap[scanId] = cmp.comparison;
          } catch {
            // Comparison may not exist for first scan — ignore
          }
        }),
      );
      setComparisons(compMap);
    } catch {
      setScans([]);
    } finally {
      setLoadingScans(false);
    }
  }, [id]);

  const loadSiteActivity = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<SiteActivityResponse>(`/sites/${id}/activity`);
      setSiteActivity(data.events ?? []);
    } catch {
      setSiteActivity([]);
    }
  }, [id]);

  const loadRetentionSummary = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<SiteRetentionResponse>(`/sites/${id}/retention`);
      setRetentionSummary(data.summary ?? null);
    } catch {
      setRetentionSummary(null);
    }
  }, [id]);

  const loadAlertPreferences = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<SiteAlertPreferencesResponse>(`/sites/${id}/alert-preferences`);
      setAlertPreferences(data.preferences ?? null);
      setAlertPreferenceDraft(data.preferences?.siteOverride ?? data.preferences?.effective ?? null);
    } catch {
      setAlertPreferences(null);
      setAlertPreferenceDraft(null);
    }
  }, [id]);

  const loadManualIssues = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<ManualIssuesResponse>(`/manual-issues?siteId=${id}`);
      setManualIssues(data.issues ?? []);
    } catch {
      // Keep the current list visible if a background refresh fails.
    }
  }, [id]);

  useEffect(() => {
    loadSite();
    loadScans();
    loadUsage();
    loadSiteActivity();
    loadRetentionSummary();
    loadAlertPreferences();
    loadManualIssues();
  }, [loadSite, loadScans, loadUsage, loadSiteActivity, loadRetentionSummary, loadAlertPreferences, loadManualIssues]);

  // Active scan detection for polling and button disable
  const hasActiveScan =
    scans.length > 0 &&
    (scans[0]?.status === 'queued' || scans[0]?.status === 'running');
  const scheduledScansLocked = usageData?.limits.scheduledScansEnabled === false;
  const historyDepth = usageData?.limits.regressionHistoryDepth ?? scans.length;
  const historyUpgradePlan = historyDepth <= 2 ? 'starter' : 'growth';
  const historyUpgradePath = buildBillingUpgradePath(historyUpgradePlan, 'regression-history-lock');
  const scanHistoryLocked = usageData !== null && scanHistoryTotal > historyDepth;
  const scheduleUpgradePath = buildBillingUpgradePath('starter', 'scheduled-scans-lock');
  const latestAuthenticatedScan = scans.find((candidate) => {
    const auth = getScanAuthMetadata(candidate);
    return auth !== null && auth.mode !== 'public';
  }) ?? null;
  const latestAuthenticatedScanAuth = latestAuthenticatedScan
    ? getScanAuthMetadata(latestAuthenticatedScan)
    : null;
  const latestScheduledAuthenticatedScan = scans.find((candidate) => {
    const auth = getScanAuthMetadata(candidate);
    return candidate.scanType === 'scheduled' && auth !== null && auth.mode !== 'public';
  }) ?? null;
  const latestScheduledAuthenticatedScanAuth = latestScheduledAuthenticatedScan
    ? getScanAuthMetadata(latestScheduledAuthenticatedScan)
    : null;
  const latestSuccessfulScan = scans.find((candidate) => (
    candidate.status === 'completed' || candidate.status === 'completed_with_failures'
  )) ?? null;
  const latestFailedScan = scans.find((candidate) => (
    candidate.status === 'failed' || candidate.status === 'auth_failed'
  )) ?? null;
  const latestFailedScanAuth = latestFailedScan ? getScanAuthMetadata(latestFailedScan) : null;
  const latestFailedScanPresentation = latestFailedScan
    ? resolveScanFailurePresentation(t, {
        status: latestFailedScan.status,
        failureReason: latestFailedScan.failureReason,
      })
    : null;
  const failedScansLast30Days = scans.filter((candidate) => (
    (candidate.status === 'failed' || candidate.status === 'auth_failed') &&
    new Date(candidate.createdAt).getTime() >= Date.now() - 30 * 24 * 60 * 60 * 1000
  )).length;
  const scheduledFailureCount = scans.filter((candidate) => (
    candidate.scanType === 'scheduled' &&
    (candidate.status === 'failed' || candidate.status === 'auth_failed')
  )).length;
  const latestOperationalFailureSummary = latestFailedScan
    ? (
        resolveScanFailureSummary(t, {
          status: latestFailedScan.status,
          failureReason: latestFailedScan.failureReason,
        })
        ?? formatAuthFailureCategory(latestFailedScanAuth?.failureCategory ?? null)
      )
    : null;
  const latestCompletedScanWithComparison = scans.find((candidate) => (
    candidate.status === 'completed' || candidate.status === 'completed_with_failures'
  ) && comparisons[candidate.id]) ?? null;
  const latestRegressionComparison = latestCompletedScanWithComparison
    ? comparisons[latestCompletedScanWithComparison.id]
    : null;
  const recoveryGuidance = latestScheduledAuthenticatedScanAuth &&
    requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory)
    ? t('ops.authGuidance')
    : latestFailedScan && latestSuccessfulScan && new Date(latestSuccessfulScan.createdAt).getTime() > new Date(latestFailedScan.createdAt).getTime()
      ? t('ops.transientGuidance')
      : latestFailedScan
        ? t('ops.generalGuidance')
        : t('ops.transientGuidance');

  // Poll silently while a scan is active
  useEffect(() => {
    if (!hasActiveScan || !id) return;
    const intervalId = setInterval(async () => {
      try {
        const data = await api.get<ScansResponse>(`/sites/${id}/scans?pageSize=20`);
        setScans(data.scans ?? []);
      } catch {
        // Ignore transient poll errors
      }
    }, 4000);
    return () => clearInterval(intervalId);
  }, [hasActiveScan, id]);

  useEffect(() => {
    if (showManualIssueModal) {
      setManualIssueError(null);
    }
  }, [showManualIssueModal]);

  const closeManualIssueModal = useCallback(() => {
    setShowManualIssueModal(false);
    setEditingManualIssue(null);
    setManualIssueSuccessMessage(null);
  }, []);

  const openCreateManualIssueModal = useCallback(() => {
    setEditingManualIssue(null);
    setManualIssueSuccessMessage(null);
    setShowManualIssueModal(true);
  }, []);

  const openEditManualIssueModal = useCallback((issue: ManualIssueListItem) => {
    setEditingManualIssue(issue);
    setManualIssueSuccessMessage(null);
    setShowManualIssueModal(true);
  }, []);

  const manualIssueSiteOptions = useMemo(
    () => site ? [{
      id: site.id,
      name: site.name,
      baseUrl: site.baseUrl,
    }] : [],
    [site?.baseUrl, site?.id, site?.name],
  );

  useEffect(() => {
    return subscribeToWorkspaceDataChanges((detail) => {
      if (detail.siteId && detail.siteId !== id) {
        return;
      }

      void loadSite();
      void loadScans();
      void loadManualIssues();
    });
  }, [id, loadManualIssues, loadScans, loadSite]);

  const handleTriggerScan = async () => {
    if (!id) return;
    setTriggering(true);
    setTriggerError(null);
    setSuccessMsg(null);
    try {
      await api.post('/scans', { siteId: id });
      setSuccessMsg(t('header.scanQueued'));
      await loadScans();
      dispatchWorkspaceDataChanged({ kind: 'scan-updated', siteId: id });
    } catch (err) {
      setTriggerError(friendlyError(err, t('header.scanTriggerFailed')));
    } finally {
      setTriggering(false);
    }
  };

  const handleCreateManualIssue = async (input: ManualIssueModalSubmitInput) => {
    setCreatingManualIssue(true);
    setManualIssueError(null);
    setManualIssueSuccessMessage(null);

    try {
      const result = await api.post<{ issue: ManualIssueListItem }>('/manual-issues', input);
      setManualIssues((current) => [result.issue, ...current.filter((issue) => issue.id !== result.issue.id)]);
      setManualIssueResetSignal((current) => current + 1);
      setManualIssueSuccessMessage(t('common:manualIssues.createSucceeded'));
      setManualIssueSuccessSignal((current) => current + 1);
      dispatchWorkspaceDataChanged({ kind: 'manual-issue-created', siteId: input.siteId });
      return true;
    } catch (err) {
      setManualIssueError(err instanceof ApiError ? err.message : t('common:manualIssues.createFailed'));
      return false;
    } finally {
      setCreatingManualIssue(false);
    }
  };

  const handleUpdateManualIssue = async (input: ManualIssueModalSubmitInput) => {
    if (!editingManualIssue) {
      return false;
    }

    setCreatingManualIssue(true);
    setManualIssueError(null);
    setManualIssueSuccessMessage(null);

    try {
      const result = await api.patch<{ issue: ManualIssueListItem }>(`/manual-issues/${editingManualIssue.id}`, {
        issueType: input.issueType,
        title: input.title,
        description: input.description,
        affectedPageUrl: input.affectedPageUrl,
        wcagVersion: input.wcagVersion,
        wcagCriterion: input.wcagCriterion,
        recommendedFix: input.recommendedFix,
        reviewBatch: input.reviewBatch,
        severity: input.severity,
        status: input.status,
      });
      setManualIssues((current) => current.map((issue) => (
        issue.id === result.issue.id ? result.issue : issue
      )));
      closeManualIssueModal();
      dispatchWorkspaceDataChanged({ kind: 'manual-issue-updated', siteId: input.siteId });
      return true;
    } catch (err) {
      setManualIssueError(err instanceof ApiError ? err.message : t('common:manualIssues.updateFailed'));
      return false;
    } finally {
      setCreatingManualIssue(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!id) return;
    setSavingSchedule(true);
    setScheduleError(null);
    setScheduleSuccess(null);
    const effectiveCron = schedulePreset === 'custom' ? customCron.trim() : schedulePreset;
    try {
      await api.patch(`/sites/${id}/config`, {
        isScheduled: scheduleEnabled,
        scheduleCron: scheduleEnabled ? (effectiveCron || null) : null,
      });
      setScheduleSuccess(t('schedule.feedback.saved'));
      await loadSite();
      setTimeout(() => setScheduleSuccess(null), 3000);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 403) {
        setScheduleError(t('schedule.feedback.locked'));
        setShowScheduleUpgradeModal(true);
        return;
      }
      setScheduleError(friendlyError(err, t('schedule.feedback.saveFailed')));
    } finally {
      setSavingSchedule(false);
    }
  };

  const canManageAlertPreferences = role === 'owner' || role === 'admin';
  const sectionItems = [
    { id: 'overview', label: t('sections.overview') },
    {
      id: 'issues',
      label: t('sections.issues'),
      badge: (manualIssues.length + (latestSuccessfulScan?.totalIssues ?? 0)) > 0
        ? manualIssues.length + (latestSuccessfulScan?.totalIssues ?? 0)
        : undefined,
    },
    { id: 'scan-history', label: t('sections.scanHistory') , badge: scanHistoryTotal > 0 ? scanHistoryTotal : undefined },
    { id: 'auth-session', label: t('sections.authSession'), badge: latestAuthenticatedScan ? t('sections.live') : undefined },
    { id: 'scheduled-jobs', label: t('sections.scheduledJobs'), badge: site?.scanConfig?.isScheduled ? t('sections.on') : undefined },
    { id: 'evidence', label: t('sections.evidence'), badge: retentionSummary?.evidence.eligibleForCleanupCount ? retentionSummary.evidence.eligibleForCleanupCount : undefined },
    { id: 'activity', label: t('sections.activity'), badge: siteActivity.length > 0 ? siteActivity.length : undefined },
  ] satisfies Array<{ id: SiteWorkspaceSection; label: string; badge?: string | number }>;

  const handleSectionChange = (section: SiteWorkspaceSection) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (section === 'overview') {
        next.delete('section');
      } else {
        next.set('section', section);
      }
      return next;
    });
  };

  const handleAlertPreferenceChange = (key: keyof AlertPreferenceMap, value: boolean) => {
    setAlertPreferenceDraft((prev) => ({
      scanCompleted: prev?.scanCompleted ?? alertPreferences?.effective.scanCompleted ?? true,
      scanFailed: prev?.scanFailed ?? alertPreferences?.effective.scanFailed ?? true,
      authOperationalFailure: prev?.authOperationalFailure ?? alertPreferences?.effective.authOperationalFailure ?? true,
      issueRegression: prev?.issueRegression ?? alertPreferences?.effective.issueRegression ?? true,
      issueReappeared: prev?.issueReappeared ?? alertPreferences?.effective.issueReappeared ?? true,
      [key]: value,
    }));
  };

  const handleSaveAlertPreferences = async () => {
    if (!id || !alertPreferenceDraft) return;
    setAlertPreferencesSaving(true);
    setAlertPreferencesError(null);
    setAlertPreferencesSuccess(null);
    try {
      const data = await api.patch<SiteAlertPreferencesResponse>(
        `/sites/${id}/alert-preferences`,
        alertPreferenceDraft,
      );
      setAlertPreferences(data.preferences ?? null);
      setAlertPreferenceDraft(data.preferences?.siteOverride ?? data.preferences?.effective ?? null);
      setAlertPreferencesSuccess(t('alertPreferences.feedback.saved'));
      setTimeout(() => setAlertPreferencesSuccess(null), 3000);
    } catch (err) {
      setAlertPreferencesError(friendlyError(err, t('alertPreferences.feedback.saveFailed')));
    } finally {
      setAlertPreferencesSaving(false);
    }
  };

  const handleResetAlertPreferences = () => {
    if (!alertPreferences) return;
    setAlertPreferenceDraft({ ...alertPreferences.organisationDefaults });
    setAlertPreferencesError(null);
    setAlertPreferencesSuccess(null);
  };

  // -------------------------------------------------------------------
  // Loading state
  // -------------------------------------------------------------------

  if (loadingSite) {
    return (
      <Container maxWidth={1240}>
          <p style={{ color: C.muted }}>{t('common:actions.loading')}</p>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Error / not found
  // -------------------------------------------------------------------

  if (siteError || !site) {
    return (
      <Container maxWidth={1240}>
          <Card>
            <p style={{ color: C.danger, marginBottom: 16 }}>{siteError ?? t('header.siteNotFound')}</p>
            <Button variant="secondary" onClick={() => navigate('/sites')}>
              {t('header.backToSites')}
            </Button>
          </Card>
      </Container>
    );
  }

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  return (
    <Container maxWidth={1320}>
      <div className="acc-site-workspace-surface">

        {/* ── Breadcrumb ─────────────────────────────────────────── */}
        <div style={{ fontSize: 13, color: C.muted, marginBottom: 16 }}>
          <Link to="/sites" style={{ color: C.link, textDecoration: 'none' }}>{t('common:navigation.sites')}</Link>
          {' › '}
          <span>{site.name}</span>
        </div>

        {/* ── Page header ────────────────────────────────────────── */}
        <div className="acc-site-workspace-surface__hero">
          <PageHeader
            eyebrow={t('header.eyebrow')}
            title={site.name}
            subtitle={
              <a
                href={site.baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: C.link, textDecoration: 'none' }}
              >
                {site.baseUrl}
              </a>
            }
            description={t('header.description')}
            meta={
              <>
                {site.scanConfig?.wcagLevel && (
                  <Badge variant="info">WCAG {site.scanConfig.wcagLevel}</Badge>
                )}
                <Badge variant={site.scanConfig?.isScheduled ? 'info' : 'neutral'}>
                  {site.scanConfig?.isScheduled ? t('header.scheduledMonitoringEnabled') : t('header.manualScanningOnly')}
                </Badge>
                {latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory) && (
                  <Badge variant="danger">{t('header.reauthenticationRequired')}</Badge>
                )}
              </>
            }
            action={
              <>
                <Link
                  to={`/sites/${id}/compliance`}
                  className="acc-link-button"
                >
                  {t('header.complianceReport')}
                </Link>
                <Button
                  size="sm"
                  onClick={handleTriggerScan}
                  disabled={triggering || hasActiveScan}
                  title={hasActiveScan ? t('header.scanInProgressTitle') : undefined}
                >
                  {triggering ? t('header.queueing') : hasActiveScan ? t('header.scanInProgress') : t('header.runScan')}
                </Button>
              </>
            }
          />
        </div>

        {/* ── Feedback banners ───────────────────────────────────── */}
        {successMsg && (
          <AlertBanner variant="info" icon="✓" style={{ marginBottom: 16 }}>
            {successMsg}
          </AlertBanner>
        )}
        {triggerError && (
          <AlertBanner variant="danger" style={{ marginBottom: 16 }}>
            {triggerError}
          </AlertBanner>
        )}

        <div className="acc-site-workspace-surface__tabs">
          <SectionTabs
            items={sectionItems}
            activeId={activeSection}
            onChange={(sectionId) => handleSectionChange(sectionId as SiteWorkspaceSection)}
            sticky
          />
        </div>

        {activeSection === 'overview' && (
          <>
        {/* ── KPI strip ──────────────────────────────────────────── */}
        {scans.length > 0 && (() => {
          const latest = scans[0];
          const isCompleted =
            latest.status === 'completed' || latest.status === 'completed_with_failures';
          const score = isCompleted
            ? estimateAccessibilityScore(latest.criticalIssues ?? 0, latest.totalIssues ?? 0)
            : null;

          return (
            <div className="acc-site-workspace-surface__kpis">
              {/* Score */}
              <div
                style={{
                  background: C.surface,
                  border: '1px solid var(--acc-color-border)',
                  borderRadius: 16,
                  padding: '18px 20px',
                  boxShadow: 'var(--acc-shadow-xs)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('overview.kpis.score')}
                </div>
                {score !== null ? (
                  <>
                    <div style={{ fontSize: 28, fontWeight: 800, color: getScoreColor(score), lineHeight: 1 }}>
                      {score}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <Badge variant={getScoreVariant(score)}>
                        {t(`common:${getScoreLabelKey(score)}`)}
                      </Badge>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 22, fontWeight: 700, color: C.muted }}>—</div>
                )}
              </div>

              {/* Issues */}
              <div
                style={{
                  background: C.surface,
                  border: '1px solid var(--acc-color-border)',
                  borderRadius: 16,
                  padding: '18px 20px',
                  boxShadow: 'var(--acc-shadow-xs)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('overview.kpis.issues')}
                </div>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    color: (latest.totalIssues ?? 0) > 0 ? C.text : C.success,
                    lineHeight: 1,
                  }}
                >
                  {latest.totalIssues ?? '—'}
                </div>
                {(latest.criticalIssues ?? 0) > 0 && (
                  <div style={{ fontSize: 12, color: C.danger, marginTop: 6, fontWeight: 600 }}>
                    {t('overview.kpis.criticalCount', { count: latest.criticalIssues })}
                  </div>
                )}
              </div>

              {/* Pages */}
              <div
                style={{
                  background: C.surface,
                  border: '1px solid var(--acc-color-border)',
                  borderRadius: 16,
                  padding: '18px 20px',
                  boxShadow: 'var(--acc-shadow-xs)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('overview.kpis.pages')}
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                  {latest.pagesScanned ?? '—'}
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{t('overview.kpis.pagesScanned')}</div>
              </div>

              {/* Scan status */}
              <div
                style={{
                  background: C.surface,
                  border: '1px solid var(--acc-color-border)',
                  borderRadius: 16,
                  padding: '18px 20px',
                  boxShadow: 'var(--acc-shadow-xs)',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  {t('overview.kpis.latestScan')}
                </div>
                <Badge variant={statusVariant(latest.status)}>
                  {formatScanStatus(latest.status)}
                </Badge>
                {(latest.completedAt || latest.failedAt) && (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                    {new Date(latest.completedAt ?? latest.failedAt ?? latest.createdAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── Health summary ─────────────────────────────────────── */}
        {scans.length > 0 && (() => {
          const latest = scans[0];
          const isCompleted =
            latest.status === 'completed' || latest.status === 'completed_with_failures';
          const score = isCompleted
            ? estimateAccessibilityScore(latest.criticalIssues ?? 0, latest.totalIssues ?? 0)
            : null;

          if (!isCompleted && latest.status !== 'failed' && latest.status !== 'auth_failed') return null;

          let icon = '✓';
          let title = t('overview.health.goodTitle');
          let body = t('overview.health.goodBody');
          let variant: 'info' | 'warning' | 'danger' | 'neutral' = 'info';

          if (latest.status === 'auth_failed') {
            icon = '✕';
            title = t('overview.health.authLostTitle');
            body = latest.failureReason
              ? t('overview.health.reason', { reason: latest.failureReason })
              : t('overview.health.authLostBody');
            variant = 'danger';
          } else if (latest.status === 'failed') {
            icon = '✕';
            title = t('overview.health.failedTitle');
            body = latestFailedScanPresentation
              ? latestFailedScanPresentation.nextStep
                ? `${latestFailedScanPresentation.body} ${latestFailedScanPresentation.nextStep}`
                : latestFailedScanPresentation.body
              : t('overview.health.failedBody');
            variant = 'danger';
          } else if (score !== null && score < 60) {
            icon = '🔴';
            title = t('overview.health.highRiskTitle');
            body = t('overview.health.highRiskBody', { score, count: latest.criticalIssues });
            variant = 'danger';
          } else if (score !== null && score < 75) {
            icon = '⚠';
            title = t('overview.health.needsAttentionTitle');
            body = t('overview.health.needsAttentionBody', { score });
            variant = 'warning';
          }

          return (
            <RecommendationPanel
              icon={icon}
              title={title}
              body={body}
              variant={variant}
              style={{ marginBottom: 20 }}
            />
          );
        })()}

        {(latestSuccessfulScan || latestFailedScan || latestAuthenticatedScan) && (
          <Card title={t('ops.title')} style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('ops.lastSuccessful')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                  {latestSuccessfulScan
                    ? formatDate(latestSuccessfulScan.completedAt ?? latestSuccessfulScan.createdAt)
                    : t('ops.never')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('ops.lastFailure')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: latestFailedScan ? C.danger : C.text }}>
                  {latestFailedScan
                    ? formatDate(latestFailedScan.failedAt ?? latestFailedScan.createdAt)
                    : t('ops.noneRecorded')}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('ops.failedLast30Days')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: failedScansLast30Days > 0 ? 'var(--acc-color-warning)' : C.text }}>
                  {failedScansLast30Days}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('ops.scheduledFailures')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: scheduledFailureCount > 0 ? 'var(--acc-color-warning)' : C.text }}>
                  {scheduledFailureCount}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('ops.authAttention')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory) ? C.danger : C.text }}>
                  {latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory)
                    ? formatAuthFailureCategory(latestScheduledAuthenticatedScanAuth.failureCategory) ?? t('ops.noneRecorded')
                    : t('ops.noneRecorded')}
                </div>
              </div>
            </div>

            {latestFailedScan && latestOperationalFailureSummary && (
              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 8 }}>
                {t('ops.failureSummary', { summary: latestOperationalFailureSummary })}
              </div>
            )}
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, marginBottom: 16 }}>
              {t('ops.recoverySummary', { summary: recoveryGuidance })}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                onClick={handleTriggerScan}
                disabled={triggering || hasActiveScan}
                title={hasActiveScan ? t('header.scanInProgressTitle') : undefined}
              >
                {triggering ? t('header.queueing') : hasActiveScan ? t('header.scanInProgress') : t('ops.retryScan')}
              </Button>
              {(latestFailedScan || latestSuccessfulScan) && (
                <Link
                  to={`/scans/${(latestFailedScan ?? latestSuccessfulScan)!.id}`}
                  style={tbl.link}
                >
                  {t('ops.reviewHistory')}
                </Link>
              )}
              {latestAuthenticatedScan && (
                <Link to={`/scans/${latestAuthenticatedScan.id}`} style={tbl.link}>
                  {t('ops.reviewAuth')}
                </Link>
              )}
            </div>
          </Card>
        )}

        {latestRegressionComparison && latestCompletedScanWithComparison && (
          <Card title={t('regression.title')} style={{ marginBottom: 20 }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                    {t('regression.latestComparisonState')}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: latestRegressionComparison.detail.summary?.state === 'regressed' ? C.danger : latestRegressionComparison.detail.summary?.state === 'improved' ? C.success : C.text }}>
                    {formatRegressionState(latestRegressionComparison.detail.summary?.state)}
                  </div>
                </div>
                <Badge
                  variant={
                    latestRegressionComparison.detail.summary?.state === 'regressed'
                      ? 'danger'
                      : latestRegressionComparison.detail.summary?.state === 'improved'
                        ? 'success'
                        : 'info'
                  }
                >
                  {t('regression.scoreDelta', { value: `${latestRegressionComparison.scoreDelta > 0 ? '+' : ''}${latestRegressionComparison.scoreDelta}` })}
                </Badge>
              </div>

              <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65 }}>
                {latestRegressionComparison.detail.summary?.state
                  ? formatRegressionSummary(latestRegressionComparison.detail.summary.state)
                  : t('regression.summaryFallback')}
              </div>

              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: C.muted }}>
                <span>{t('regression.issuesDelta', { value: `${latestRegressionComparison.issuesDelta > 0 ? '+' : ''}${latestRegressionComparison.issuesDelta}` })}</span>
                <span>{t('regression.criticalDelta', { value: `${latestRegressionComparison.criticalDelta > 0 ? '+' : ''}${latestRegressionComparison.criticalDelta}` })}</span>
                <span>{t('regression.reappearedPatterns', { count: latestRegressionComparison.detail.reappeared?.length ?? 0 })}</span>
              </div>

              <div>
                <Link to={`/scans/${latestCompletedScanWithComparison.id}`} style={tbl.link}>{t('regression.openComparedScan')}</Link>
              </div>
            </div>
          </Card>
        )}
          </>
        )}

        {activeSection === 'issues' && (
          <>
            <Card style={{ marginBottom: 20 }}>
              <SectionHeader
                eyebrow={t('common:manualIssues.eyebrow')}
                title={t('common:manualIssues.tableTitle')}
                subtitle={t('common:manualIssues.tableDescription')}
                action={
                  <Button size="sm" onClick={openCreateManualIssueModal}>
                    {t('common:manualIssues.addAction')}
                  </Button>
                }
              />

              {manualIssues.length === 0 ? (
                <EmptyState
                  icon="✍️"
                  title={t('common:manualIssues.emptyTitle')}
                  description={t('common:manualIssues.emptyDescriptionSite')}
                  action={
                    <Button onClick={openCreateManualIssueModal}>
                      {t('common:manualIssues.addAction')}
                    </Button>
                  }
                />
              ) : (
                  <ManualIssuesTable
                    issues={manualIssues}
                    caption={t('common:manualIssues.tableCaptionSite')}
                    exportScopeName={site?.name ?? 'site'}
                    onEdit={openEditManualIssueModal}
                  />
              )}
            </Card>

            <Card style={{ marginBottom: 20 }}>
              <SectionHeader
                eyebrow={t('issues.eyebrow')}
                title={t('issues.title')}
                subtitle={t('issues.subtitle')}
                action={
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Button variant="secondary" onClick={openCreateManualIssueModal}>
                      {t('common:manualIssues.addAction')}
                    </Button>
                    {latestSuccessfulScan && (
                      <>
                        <Link to={`/scans/${latestSuccessfulScan.id}`} style={{ textDecoration: 'none' }}>
                          <Button>{t('issues.openTriage')}</Button>
                        </Link>
                        <Link to={`/sites/${site.id}/compliance`} style={{ textDecoration: 'none' }}>
                          <Button variant="secondary">{t('issues.openComplianceReport')}</Button>
                        </Link>
                      </>
                    )}
                  </div>
                }
              />

              {!latestSuccessfulScan ? (
                <EmptyState
                  icon="🗂"
                  title={t('issues.emptyTitle')}
                  description={t('issues.emptyDescription')}
                  action={
                    <Button onClick={handleTriggerScan} disabled={triggering || hasActiveScan}>
                      {triggering ? t('header.queueing') : hasActiveScan ? t('header.scanInProgress') : t('issues.runFirstScan')}
                    </Button>
                  }
                />
              ) : (
                <div style={{ display: 'grid', gap: 16 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: 14,
                    }}
                  >
                    <div className="acc-summary-list__row">
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                          {t('issues.latestScan')}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>
                          {latestSuccessfulScan.status === 'completed_with_failures' ? t('issues.completeWithFailures') : t('issues.completed')}
                        </div>
                      </div>
                    </div>
                    <div className="acc-summary-list__row">
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                          {t('issues.criticalIssues')}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: latestSuccessfulScan.criticalIssues > 0 ? 'var(--acc-color-danger)' : C.text }}>
                          {latestSuccessfulScan.criticalIssues}
                        </div>
                      </div>
                    </div>
                    <div className="acc-summary-list__row">
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                          {t('issues.totalIssues')}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>
                          {latestSuccessfulScan.totalIssues}
                        </div>
                      </div>
                    </div>
                    <div className="acc-summary-list__row">
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                          {t('issues.pagesScanned')}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, color: C.text }}>
                          {latestSuccessfulScan.pagesScanned}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gap: 10 }}>
                    <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
                      {t('issues.latestRunSummary', { date: formatDate(latestSuccessfulScan.completedAt ?? latestSuccessfulScan.createdAt) })}
                    </div>
                    {latestRegressionComparison && (
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Badge variant={latestRegressionComparison.scoreDelta < 0 ? 'danger' : latestRegressionComparison.scoreDelta > 0 ? 'success' : 'neutral'}>
                          {t('issues.scoreDelta', { value: `${latestRegressionComparison.scoreDelta > 0 ? '+' : ''}${latestRegressionComparison.scoreDelta}` })}
                        </Badge>
                        <Badge variant={latestRegressionComparison.criticalDelta > 0 ? 'danger' : latestRegressionComparison.criticalDelta < 0 ? 'success' : 'neutral'}>
                          {t('issues.criticalDelta', { value: `${latestRegressionComparison.criticalDelta > 0 ? '+' : ''}${latestRegressionComparison.criticalDelta}` })}
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          </>
        )}

        {activeSection === 'scan-history' && (
          <>
        {/* ── Scan configuration ─────────────────────────────────── */}
        <Card title={t('scanHistory.configTitle')} style={{ marginBottom: 20 }}>
          {/* Static config values — only shown when a config row exists */}
          {site.scanConfig && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                gap: 20,
                marginBottom: 24,
              }}
            >
              {[
                { label: t('scanHistory.configLabels.wcagLevel'), value: site.scanConfig.wcagLevel },
                { label: t('scanHistory.configLabels.maxPages'),  value: site.scanConfig.maxPages },
                { label: t('scanHistory.configLabels.maxDepth'),  value: site.scanConfig.maxDepth },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Schedule controls (Phase 22) — ALWAYS visible ───────── */}
          <div style={{ borderTop: site.scanConfig ? `1px solid ${C.border}` : 'none', paddingTop: site.scanConfig ? 20 : 0 }}>
            {scheduledScansLocked ? (
              <LockedFeatureState
                withCard={false}
                tierLabel="Starter+"
                title={t('schedule.locked.title')}
                description={t('schedule.locked.description')}
                benefits={[
                  {
                    title: t('schedule.locked.benefits.automate.title'),
                    body: t('schedule.locked.benefits.automate.body'),
                  },
                  {
                    title: t('schedule.locked.benefits.keepFresh.title'),
                    body: t('schedule.locked.benefits.keepFresh.body'),
                  },
                  {
                    title: t('schedule.locked.benefits.repeatable.title'),
                    body: t('schedule.locked.benefits.repeatable.body'),
                  },
                ]}
                upgradeLabel={t('schedule.locked.upgradeLabel')}
                onUpgrade={() => navigate(scheduleUpgradePath)}
                onPricing={() => navigate('/pricing')}
              />
            ) : (
              <>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 14 }}>
                  {t('schedule.title')}
                </div>

                {/* Enable toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: 'var(--acc-color-primary)', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: C.text }}>
                    {t('schedule.enable')}
                  </span>
                </label>

                {scheduleEnabled && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    <div>
                      <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 5 }}>
                        {t('schedule.frequency')}
                      </label>
                      <select
                        className="acc-select"
                        value={schedulePreset}
                        onChange={(e) => {
                          setSchedulePreset(e.target.value);
                          if (e.target.value !== 'custom') setScheduleCron(e.target.value);
                        }}
                        style={{ maxWidth: 280 }}
                      >
                        {frequencyPresets.map((p) => (
                          <option key={p.cron} value={p.cron}>{p.label}</option>
                        ))}
                      </select>
                    </div>

                    {schedulePreset === 'custom' && (
                      <div>
                        <label style={{ fontSize: 12, color: C.muted, fontWeight: 600, display: 'block', marginBottom: 5 }}>
                          {t('schedule.cronExpression')}
                        </label>
                        <input
                          className="acc-input"
                          type="text"
                          placeholder={t('schedule.cronPlaceholder')}
                          value={customCron}
                          onChange={(e) => setCustomCron(e.target.value)}
                          style={{ maxWidth: 280, fontFamily: 'monospace', fontSize: 13 }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Last / Next run timestamps — shown when scheduling is already active */}
                {site.scanConfig?.isScheduled && (
                  <div style={{ display: 'flex', gap: 32, marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t('schedule.lastRun')}</div>
                      <div style={{ fontSize: 13, color: C.text }}>
                        {site.scanConfig.lastScheduledRunAt ? formatDate(site.scanConfig.lastScheduledRunAt) : '—'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{t('schedule.nextRun')}</div>
                      <div style={{ fontSize: 13, color: C.text }}>
                        {site.scanConfig.nextScheduledRunAt ? formatDate(site.scanConfig.nextScheduledRunAt) : '—'}
                      </div>
                    </div>
                  </div>
                )}

                {scheduleError && (
                  <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{scheduleError}</div>
                )}
                {scheduleSuccess && (
                  <div style={{ color: C.success, fontSize: 12, marginBottom: 10 }}>{scheduleSuccess}</div>
                )}

                <Button
                  variant="secondary"
                  onClick={handleSaveSchedule}
                  disabled={savingSchedule}
                >
                  {savingSchedule ? t('common:actions.saving') : t('schedule.save')}
                </Button>
              </>
            )}
          </div>
        </Card>
          </>
        )}

        {activeSection === 'auth-session' && (
          <>
        <div style={{ marginBottom: 20 }}>
          <AuthenticatedScanAccessCard
            siteId={site.id}
            planLabel={getPlanLabel(usageData?.plan)}
            featureEnabled={usageData ? usageData.limits.authenticatedScanningEnabled : true}
            role={role}
          />
        </div>

        {latestAuthenticatedScan && latestAuthenticatedScanAuth && (
          <Card title={t('authSession.title')} style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.lastAuthStateUsed')}
                </div>
                <Badge
                  variant={
                    latestAuthenticatedScan.status === 'auth_failed' || latestAuthenticatedScanAuth.blockedByAuthFailure
                      ? 'danger'
                      : 'info'
                  }
                >
                  {formatAuthMode(latestAuthenticatedScanAuth.mode)}
                </Badge>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.latestResult')}
                </div>
                <Badge variant={statusVariant(latestAuthenticatedScan.status)}>
                  {formatScanStatus(latestAuthenticatedScan.status)}
                </Badge>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.lastAuthFailureReason')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: latestAuthenticatedScanAuth.failureCategory ? C.danger : C.text }}>
                  {formatAuthFailureCategory(latestAuthenticatedScanAuth.failureCategory) ?? t('authSession.noneRecorded')}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.lastAuthenticatedPagesScanned')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                  {latestAuthenticatedScanAuth.authenticatedPagesScanned || latestAuthenticatedScan.pagesScanned}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.authenticatedRoutesDiscovered')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>
                  {latestAuthenticatedScanAuth.discoveredRouteCount || latestAuthenticatedScan.pagesDiscovered}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.lastScheduledAuthScan')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: latestScheduledAuthenticatedScan ? C.text : C.muted }}>
                  {latestScheduledAuthenticatedScan
                    ? formatScanStatus(latestScheduledAuthenticatedScan.status)
                    : t('authSession.noScheduledAuthRunYet')}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('authSession.reauthenticationRequired')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory) ? C.danger : C.text }}>
                  {latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory)
                    ? t('authSession.yes')
                    : t('authSession.no')}
                </div>
              </div>
            </div>

            <AlertBanner
              variant={
                latestAuthenticatedScan.status === 'auth_failed' || latestAuthenticatedScanAuth.blockedByAuthFailure
                  ? 'danger'
                  : latestAuthenticatedScan.status === 'completed_with_failures'
                    ? 'warning'
                    : 'info'
              }
              icon={
                latestAuthenticatedScan.status === 'auth_failed' || latestAuthenticatedScanAuth.blockedByAuthFailure
                  ? '✕'
                  : latestAuthenticatedScan.status === 'completed_with_failures'
                    ? '⚠'
                    : '✓'
              }
              style={{ marginBottom: 12 }}
            >
              {t('authSession.messageFallback', { mode: formatAuthMode(latestAuthenticatedScanAuth.mode).toLowerCase() })}
            </AlertBanner>

            {latestAuthenticatedScanAuth.consoleHint && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                {t('authSession.consoleHint', { hint: latestAuthenticatedScanAuth.consoleHint })}
              </div>
            )}
            {latestAuthenticatedScanAuth.networkHint && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                {t('authSession.networkHint', { hint: latestAuthenticatedScanAuth.networkHint })}
              </div>
            )}
            {latestAuthenticatedScanAuth.refreshAttempted && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                {latestAuthenticatedScanAuth.refreshSucceeded
                  ? t('authSession.refreshSucceeded')
                  : t('authSession.refreshFailed')}
              </div>
            )}
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
              {(
                (latestAuthenticatedScanAuth.authenticatedPagesScanned || latestAuthenticatedScan.pagesScanned) > 1 ||
                (latestAuthenticatedScanAuth.discoveredRouteCount || latestAuthenticatedScan.pagesDiscovered) > 1
              )
                ? t('authSession.coverageExpanded', {
                  pages: latestAuthenticatedScanAuth.authenticatedPagesScanned || latestAuthenticatedScan.pagesScanned,
                  routes: latestAuthenticatedScanAuth.discoveredRouteCount || latestAuthenticatedScan.pagesDiscovered,
                })
                : t('authSession.coverageLimited')}
            </div>
            {latestScheduledAuthenticatedScan && (
              <div style={{ fontSize: 12, color: latestScheduledAuthenticatedScanAuth && requiresReauthentication(latestScheduledAuthenticatedScanAuth.failureCategory) ? C.danger : C.muted, marginBottom: 4 }}>
                {t('authSession.latestScheduledRun', {
                  date: formatDate(
                    latestScheduledAuthenticatedScan.completedAt ??
                    latestScheduledAuthenticatedScan.failedAt ??
                    latestScheduledAuthenticatedScan.createdAt,
                  ),
                })}
                {latestScheduledAuthenticatedScanAuth?.failureCategory
                  ? ` · ${formatAuthFailureCategory(latestScheduledAuthenticatedScanAuth.failureCategory)}`
                  : ''}
              </div>
            )}

            <div style={{ fontSize: 12, color: C.muted, marginTop: 12 }}>
              {t('authSession.lastRun', {
                date: formatDate(latestAuthenticatedScan.completedAt ?? latestAuthenticatedScan.failedAt ?? latestAuthenticatedScan.createdAt),
              })}
            </div>
            <div style={{ marginTop: 8 }}>
              <Link to={`/scans/${latestAuthenticatedScan.id}`} style={tbl.link}>{t('authSession.openScanDetails')}</Link>
            </div>
          </Card>
        )}
          </>
        )}

        {activeSection === 'evidence' && (
          <>
        {retentionSummary && (
          <Card title={t('evidence.title')} style={{ marginBottom: 20 }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('evidence.activeEvidence')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{retentionSummary.evidence.activeCount}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('evidence.archivedEvidence')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{retentionSummary.evidence.archivedCount}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('evidence.cleanupCandidates')}
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: retentionSummary.evidence.eligibleForCleanupCount > 0 ? 'var(--acc-color-warning)' : C.text }}>
                  {retentionSummary.evidence.eligibleForCleanupCount}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                  {t('evidence.latestCompletedScan')}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: retentionSummary.latestCompletedScanId ? C.text : C.muted }}>
                  {retentionSummary.latestCompletedScanId ? t('evidence.retainedForTraceability') : t('evidence.noCompletedScanYet')}
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surfaceMuted, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>{t('evidence.latestAuthTestArtifacts')}</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  {t('evidence.screenshot')}: {retentionSummary.diagnostics.latestTest.screenshotAvailable ? t('evidence.available') : t('evidence.notCaptured')}
                  <br />
                  {t('evidence.status')}: {retentionSummary.diagnostics.latestTest.retentionStatus ?? t('evidence.notAvailable')}
                  <br />
                  {t('evidence.cleanupReview')}: {formatDate(retentionSummary.diagnostics.latestTest.cleanupEligibleAt)}
                </div>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.surfaceMuted, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>{t('evidence.latestAuthenticatedCrawlArtifacts')}</div>
                <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                  {t('evidence.screenshot')}: {retentionSummary.diagnostics.latestAuthenticatedCrawl.screenshotAvailable ? t('evidence.available') : t('evidence.notCaptured')}
                  <br />
                  {t('evidence.status')}: {retentionSummary.diagnostics.latestAuthenticatedCrawl.retentionStatus ?? t('evidence.notAvailable')}
                  <br />
                  {t('evidence.cleanupReview')}: {formatDate(retentionSummary.diagnostics.latestAuthenticatedCrawl.cleanupEligibleAt)}
                </div>
              </div>
            </div>

            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              {retentionSummary.policy.notes}
            </div>
          </Card>
        )}
          </>
        )}

        {activeSection === 'scheduled-jobs' && (
          <>
        <Card title={t('scheduledJobs.title')} style={{ marginBottom: 20 }}>
          <div style={{ display: 'grid', gap: 14 }}>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
              {t('scheduledJobs.description')}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12,
              }}
            >
              <div className="acc-summary-list__row">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                    {t('scheduledJobs.schedulePosture')}
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>
                    {site.scanConfig?.isScheduled ? t('scheduledJobs.automated') : t('scheduledJobs.manualOnly')}
                  </div>
                </div>
              </div>
              <div className="acc-summary-list__row">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                    {t('scheduledJobs.nextScheduledRun')}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                    {formatDate(site.scanConfig?.nextScheduledRunAt ?? null)}
                  </div>
                </div>
              </div>
              <div className="acc-summary-list__row">
                <div>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 5 }}>
                    {t('scheduledJobs.lastScheduledRun')}
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                    {formatDate(site.scanConfig?.lastScheduledRunAt ?? null)}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => handleSectionChange('scan-history')}
                style={{
                  minHeight: 40,
                  padding: '0 14px',
                  borderRadius: 12,
                  border: '1px solid var(--acc-color-border)',
                  background: C.surface,
                  color: C.text,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {t('scheduledJobs.manageSchedule')}
              </button>
            </div>
          </div>
        </Card>

        {alertPreferences && alertPreferenceDraft && (
          <Card title={t('alertPreferences.title')} style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>
              {t('alertPreferences.description')}
            </div>

            <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
              {(Object.keys(alertPreferenceDraft) as Array<keyof AlertPreferenceMap>).map((key) => (
                <label
                  key={key}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'flex-start',
                    padding: '12px 14px',
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    background: C.surface,
                    cursor: canManageAlertPreferences ? 'pointer' : 'default',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={alertPreferenceDraft[key]}
                    disabled={!canManageAlertPreferences}
                    onChange={(event) => handleAlertPreferenceChange(key, event.target.checked)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontWeight: 600, color: C.text, fontSize: 13, marginBottom: 4 }}>
                      {preferenceLabel(key)}
                    </span>
                    <span style={{ display: 'block', color: C.muted, fontSize: 12, lineHeight: 1.5 }}>
                      {preferenceHint(key)}
                    </span>
                    <span style={{ display: 'block', color: C.muted, fontSize: 11, marginTop: 4 }}>
                      {t('alertPreferences.organizationDefault', {
                        value: alertPreferences.organisationDefaults[key]
                          ? t('alertPreferences.defaultEnabled')
                          : t('alertPreferences.defaultMuted'),
                      })}
                    </span>
                  </span>
                </label>
              ))}
            </div>

            {alertPreferencesError && (
              <div style={{ color: C.danger, fontSize: 12, marginBottom: 10 }}>{alertPreferencesError}</div>
            )}
            {alertPreferencesSuccess && (
              <div style={{ color: C.success, fontSize: 12, marginBottom: 10 }}>{alertPreferencesSuccess}</div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {canManageAlertPreferences ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={handleResetAlertPreferences}
                    disabled={alertPreferencesSaving}
                  >
                    {t('alertPreferences.reset')}
                  </Button>
                  <Button
                    onClick={handleSaveAlertPreferences}
                    disabled={alertPreferencesSaving}
                  >
                    {alertPreferencesSaving ? t('common:actions.saving') : t('alertPreferences.save')}
                  </Button>
                </>
              ) : (
                <div style={{ fontSize: 12, color: C.muted }}>
                  {t('alertPreferences.cannotManage')}
                </div>
              )}
            </div>
          </Card>
        )}
          </>
        )}

        {activeSection === 'activity' && (
          <>
        <Card title={t('activity.title')} style={{ marginBottom: 20 }}>
          {siteActivity.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted }}>
              {t('activity.empty')}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {siteActivity.slice(0, 12).map((event) => (
                <div
                  key={event.id}
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    background: C.surface,
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>
                      {formatAuditAction(event.action)}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted }}>
                      {formatDate(event.createdAt)}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                    {describeAuditEvent(event)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
          </>
        )}

        {activeSection === 'scan-history' && (
          <>
        {/* ── Scan history ───────────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <SectionHeader
            title={t('scanHistory.sectionTitle')}
            subtitle={t('scanHistory.sectionSubtitle')}
          />

          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {loadingScans && (
              <p style={{ color: C.muted, fontSize: 13, padding: '16px 24px', margin: 0 }}>
                {t('scanHistory.loading')}
              </p>
            )}

            {!loadingScans && scans.length === 0 && (
              <EmptyState
                icon="🔍"
                title={t('scanHistory.emptyTitle')}
                description={t('scanHistory.emptyDescription')}
                action={
                  <Button onClick={handleTriggerScan} disabled={triggering}>
                    {triggering ? t('header.queueing') : t('scanHistory.runFirstScan')}
                  </Button>
                }
                style={{ border: 'none', boxShadow: 'none', borderRadius: 0 }}
              />
            )}

            {!loadingScans && scans.length > 0 && (
              <table style={tbl.table}>
                <thead>
                  <tr>
                    <th style={tbl.th}>{t('scanHistory.table.status')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.score')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.vsPrev')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.started')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.duration')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.pages')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.issues')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.critical')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.type')}</th>
                    <th style={tbl.th}>{t('scanHistory.table.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((scan) => {
                    const scanCompleted =
                      scan.status === 'completed' || scan.status === 'completed_with_failures';
                    const scanScore = scanCompleted
                      ? estimateAccessibilityScore(scan.criticalIssues ?? 0, scan.totalIssues ?? 0)
                      : null;
                    const cmp = comparisons[scan.id];
                    return (
                    <tr key={scan.id}>
                      <td style={tbl.td}>
                        <Badge variant={statusVariant(scan.status)}>
                          {formatScanStatus(scan.status)}
                        </Badge>
                        {(scan.status === 'failed' || scan.status === 'auth_failed') && scan.failureReason && (
                          <div
                            style={{
                              fontSize: 11,
                              color: C.danger,
                              marginTop: 4,
                              maxWidth: 220,
                              lineHeight: 1.4,
                            }}
                            title={resolveScanFailureSummary(t, {
                              status: scan.status,
                              failureReason: scan.failureReason,
                            }) ?? scan.failureReason}
                          >
                            {(() => {
                              const summary = resolveScanFailureSummary(t, {
                                status: scan.status,
                                failureReason: scan.failureReason,
                              }) ?? scan.failureReason;
                              return summary.length > 80 ? `${summary.slice(0, 80)}…` : summary;
                            })()}
                          </div>
                        )}
                      </td>
                      <td style={tbl.td}>
                        {scanScore !== null ? (
                          <span style={{ fontWeight: 700, color: getScoreColor(scanScore), fontSize: 14 }}>
                            {scanScore}
                          </span>
                        ) : (
                          <span style={{ color: C.muted }}>—</span>
                        )}
                      </td>
                      {/* Phase 23: delta badge */}
                      <td style={tbl.td}>
                        {cmp && cmp.baselineScanId ? (
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: cmp.scoreDelta > 0 ? C.success : cmp.scoreDelta < 0 ? C.danger : C.muted,
                            }}
                            title={t('scanHistory.deltaTitle', {
                              issues: `${cmp.issuesDelta > 0 ? '+' : ''}${cmp.issuesDelta}`,
                              critical: `${cmp.criticalDelta > 0 ? '+' : ''}${cmp.criticalDelta}`,
                            })}
                          >
                            {cmp.scoreDelta > 0 ? '▲' : cmp.scoreDelta < 0 ? '▼' : '—'}
                            {cmp.scoreDelta !== 0 ? ` ${Math.abs(cmp.scoreDelta)}` : ''}
                          </span>
                        ) : (
                          <span style={{ color: C.muted, fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ ...tbl.td, color: C.muted }}>
                        {formatDate(scan.startedAt ?? scan.createdAt)}
                      </td>
                      <td style={{ ...tbl.td, color: C.muted }}>
                        {formatDuration(scan.startedAt, scan.completedAt ?? scan.failedAt)}
                      </td>
                      <td style={{ ...tbl.td, color: C.muted }}>{scan.pagesScanned ?? '—'}</td>
                      <td style={{ ...tbl.td, color: C.muted }}>{scan.totalIssues ?? '—'}</td>
                      <td style={tbl.td}>
                        {scan.criticalIssues > 0 ? (
                          <strong style={{ color: C.danger }}>{scan.criticalIssues}</strong>
                        ) : (
                          <span style={{ color: C.muted }}>{scan.criticalIssues ?? '—'}</span>
                        )}
                      </td>
                      <td style={{ ...tbl.td, color: C.muted }} data-testid="scan-type">
                        <span>{formatScanType(scan.scanType)}</span>
                      </td>
                      <td style={tbl.td}>
                        {(scan.status === 'completed' || scan.status === 'completed_with_failures' || scan.status === 'auth_failed') && (
                          <Link to={`/scans/${scan.id}`} style={tbl.link}>{t('scanHistory.detailsLink')}</Link>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Card>
          {scanHistoryLocked && (
            <div style={{ marginTop: 12 }}>
              <LockedFeatureState
                tierLabel={t('common:plans.planTier', { plan: getPlanLabel(usageData?.plan) })}
                title={t('scanHistory.lock.title')}
                description={t('scanHistory.lock.description', { count: historyDepth })}
                benefits={[
                  {
                    title: t('scanHistory.lock.benefits.trends.title'),
                    body: t('scanHistory.lock.benefits.trends.body'),
                  },
                  {
                    title: t('scanHistory.lock.benefits.history.title'),
                    body: t('scanHistory.lock.benefits.history.body'),
                  },
                  {
                    title: t('scanHistory.lock.benefits.review.title'),
                    body: t('scanHistory.lock.benefits.review.body'),
                  },
                ]}
                upgradeLabel={t('scanHistory.lock.upgradeLabel', { plan: getPlanLabel(historyUpgradePlan) })}
                onUpgrade={() => navigate(historyUpgradePath)}
                onPricing={() => navigate('/pricing')}
              />
            </div>
          )}
        </div>
          </>
        )}
      {showScheduleUpgradeModal && (
        <FeatureLockModal
          title={t('upgradeModal.title')}
          description={t('upgradeModal.description')}
          benefits={[
            t('upgradeModal.benefits.automaticRuns'),
            t('upgradeModal.benefits.catchRegressions'),
            t('upgradeModal.benefits.repeatableCadence'),
          ]}
          upgradeLabel={t('upgradeModal.upgradeLabel')}
          onUpgrade={() => {
            setShowScheduleUpgradeModal(false);
            navigate(scheduleUpgradePath);
          }}
          onPricing={() => {
            setShowScheduleUpgradeModal(false);
            navigate('/pricing');
          }}
          onClose={() => setShowScheduleUpgradeModal(false)}
        />
      )}
      <ManualIssueModal
        open={showManualIssueModal}
        onClose={closeManualIssueModal}
        onSubmit={editingManualIssue ? handleUpdateManualIssue : handleCreateManualIssue}
        submitting={creatingManualIssue}
        error={manualIssueError}
        successMessage={manualIssueSuccessMessage}
        successSignal={manualIssueSuccessSignal}
        mode={editingManualIssue ? 'edit' : 'create'}
          initialValues={editingManualIssue ? {
            siteId: editingManualIssue.siteId,
            issueType: editingManualIssue.issueType === 'usability_issue' ? 'usability_issue' : 'wcag_issue',
            title: editingManualIssue.title,
            description: editingManualIssue.description,
            affectedPageUrl: editingManualIssue.affectedPageUrl,
            wcagVersion: editingManualIssue.wcagVersion,
            wcagCriterion: editingManualIssue.wcagCriterion,
            recommendedFix: editingManualIssue.recommendedFix,
            reviewBatch: editingManualIssue.reviewBatch,
            severity: editingManualIssue.severity,
            status: editingManualIssue.status,
          } : null}
        siteOptions={manualIssueSiteOptions}
        fixedSiteId={site?.id ?? null}
        resetSignal={manualIssueResetSignal}
      />
      </div>
    </Container>
  );
}
